<script setup lang="ts">
import QuestionnaireSelectMenu from "../parts/QuestionnaireSelectMenu.vue";
import QuestionnaireTextArea from "../parts/QuestionnaireTextArea.vue";
import QuestionnaireTextField from "../parts/QuestionnaireTextField.vue";
import QuestionnaireButtonPrev from "../parts/QuestionnaireButtonPrev.vue";
import QuestionnaireButtonNext from "../parts/QuestionnaireButtonNext.vue";
import { defineProps, reactive, defineEmits } from "vue";

type Props = {
  title: string;
  textAreaTitleOne: string;
  textAreaPlaceholderOne: string;
  textAreaTitleTwo: string;
  textAreaPlaceholderTwo: string;
  textAreaTitleThree: string;
  textAreaPlaceholderThree: string;
  textAreaTitleFour: string;
  textAreaPlaceholderFour: string;
  selectMenuDataOne: any;
  selectMenuDataOneTitle: string;
  textFieldOneText: string;
  textFieldOneRequired: boolean;
  textFieldOnePlaceholder: string;
  prevButtonText: string;
  nextButtonText: string;
  data?: {
    description?: string;
    whatToSchedule?: string;
    days?: string;
    missingMaterial?: string;
    dispoNotes?: string;
    fieldInstruction?: string;
  };
};

type Emits = {
  (event: "onStepBack"): void;
  (
    event: "onSubmit",
    payload: {
      description: string;
      whatToSchedule: string;
      days: string;
      missingMaterial: string;
      dispoNotes: string;
      fieldInstruction: string;
    }
  ): void;
};
const emits = defineEmits<Emits>();

const props = defineProps<Props>();

const state = reactive({
  writeWhatHappened:
    props.data && props.data.description ? props.data.description : "",
  whatDoWeSchedule:
    props.data && props.data.whatToSchedule ? props.data.whatToSchedule : "",
  howManyDays: props.data && props.data.days ? props.data.days : "",
  whatMaterialIsMissing:
    props.data && props.data.missingMaterial ? props.data.missingMaterial : "",
  dispoNotes: props.data && props.data.dispoNotes ? props.data.dispoNotes : "",
  whatNeedsToBeDone:
    props.data && props.data.fieldInstruction
      ? props.data.fieldInstruction
      : "",
});

interface ErrorField {
  value: string;
  isError: boolean;
}

type ErrorsState = Record<string, ErrorField>;

const errors = reactive<ErrorsState>({
  writeWhatHappened: {
    value: "",
    isError: false,
  },
  whatDoWeSchedule: {
    value: "",
    isError: false,
  },
  howManyDays: {
    value: "",
    isError: false,
  },
  whatMaterialIsMissing: {
    value: "",
    isError: false,
  },
  dispoNotes: {
    value: "",
    isError: false,
  },
  whatNeedsToBeDone: {
    value: "",
    isError: false,
  },
});

const validate = () => {
  if (state.writeWhatHappened) {
    if (state.writeWhatHappened.trim().length > 255) {
      errors.writeWhatHappened.value = "Maximum number of characters is 255.";
      errors.writeWhatHappened.isError = true;
    } else {
      errors.writeWhatHappened.value = "";
      errors.writeWhatHappened.isError = false;
    }
  } else {
    errors.writeWhatHappened.value = "This field is required.";
    errors.writeWhatHappened.isError = true;
  }

  if (state.whatDoWeSchedule) {
    if (state.whatDoWeSchedule.trim().length > 255) {
      errors.whatDoWeSchedule.value = "Maximum number of characters is 255.";
      errors.whatDoWeSchedule.isError = true;
    } else {
      errors.whatDoWeSchedule.value = "";
      errors.whatDoWeSchedule.isError = false;
    }
  } else {
    errors.whatDoWeSchedule.value = "This field is required.";
    errors.whatDoWeSchedule.isError = true;
  }

  if (state.whatMaterialIsMissing) {
    if (state.whatMaterialIsMissing.trim().length > 255) {
      errors.whatMaterialIsMissing.value =
        "Maximum number of characters is 255.";
      errors.whatMaterialIsMissing.isError = true;
    } else {
      errors.whatMaterialIsMissing.value = "";
      errors.whatMaterialIsMissing.isError = false;
    }
  }

  if (state.dispoNotes) {
    if (state.dispoNotes.trim().length > 255) {
      errors.dispoNotes.value = "Maximum number of characters is 255.";
      errors.dispoNotes.isError = true;
    } else {
      errors.dispoNotes.value = "";
      errors.dispoNotes.isError = false;
    }
  }

  if (state.whatNeedsToBeDone) {
    if (state.whatNeedsToBeDone.trim().length > 255) {
      errors.whatNeedsToBeDone.value = "Maximum number of characters is 255.";
      errors.whatNeedsToBeDone.isError = true;
    } else {
      errors.whatNeedsToBeDone.value = "";
      errors.whatNeedsToBeDone.isError = false;
    }
  } else {
    errors.whatNeedsToBeDone.value = "This field is required.";
    errors.whatNeedsToBeDone.isError = true;
  }

  let keys = Object.keys(errors);
  let invalidFields = keys.filter((item: string) => errors[item].isError);
  return invalidFields.length === 0;
};

const onSuccess = () => {
  state.writeWhatHappened = "";
  state.whatDoWeSchedule = "";
  state.howManyDays = "";
  state.whatMaterialIsMissing = "";
  state.dispoNotes = "";
  state.whatNeedsToBeDone = "";
};

const onSubmit = () => {
  if (!validate()) return;

  const data = {
    description: state.writeWhatHappened,
    whatToSchedule: state.whatDoWeSchedule,
    days: state.howManyDays,
    missingMaterial: state.whatMaterialIsMissing,
    dispoNotes: state.dispoNotes,
    fieldInstruction: state.whatNeedsToBeDone,
  };

  emits("onSubmit", data);
  onSuccess();
};
</script>

<template>
  <div class="w-full">
    <div class="w-full pt-[24px] px-[32px] max-h-[450px] overflow-y-auto">
      <div>
        <h2
          class="pb-2 text-xl leading-8 tracking-[0.01rem] font-semibold text-primary-text-color"
        >
          {{ title }}
        </h2>
        <div class="mb-2">
          <QuestionnaireTextArea
            v-model="state.writeWhatHappened"
            :title="textAreaTitleOne"
            :placeholder="textAreaPlaceholderOne"
            :is-error="errors.writeWhatHappened.isError"
            :error-message="errors.writeWhatHappened.value"
          />
        </div>
        <div class="mb-2">
          <QuestionnaireSelectMenu
            v-model="state.whatDoWeSchedule"
            :title="selectMenuDataOneTitle"
            :options="selectMenuDataOne"
            :is-error="errors.whatDoWeSchedule.isError"
            :error-message="errors.whatDoWeSchedule.value"
          />
        </div>
        <div class="mb-2">
          <QuestionnaireTextField
            v-model="state.howManyDays"
            :title="textFieldOneText"
            :is-required="textFieldOneRequired"
            :placeholder="textFieldOnePlaceholder"
            :is-error="errors.howManyDays.isError"
            :error-message="errors.howManyDays.value"
          />
        </div>
        <div class="mb-2">
          <QuestionnaireTextArea
            v-model="state.whatMaterialIsMissing"
            :title="textAreaTitleTwo"
            :placeholder="textAreaPlaceholderTwo"
            :is-error="errors.whatMaterialIsMissing.isError"
            :error-message="errors.whatMaterialIsMissing.value"
          />
        </div>
        <div class="mb-2">
          <QuestionnaireTextArea
            v-model="state.dispoNotes"
            :title="textAreaTitleThree"
            :placeholder="textAreaPlaceholderThree"
            :is-error="errors.dispoNotes.isError"
            :error-message="errors.dispoNotes.value"
          />
        </div>
        <div>
          <QuestionnaireTextArea
            v-model="state.whatNeedsToBeDone"
            :title="textAreaTitleFour"
            :placeholder="textAreaPlaceholderFour"
            :is-error="errors.whatNeedsToBeDone.isError"
            :error-message="errors.whatNeedsToBeDone.value"
          />
        </div>
      </div>
    </div>
    <div
      class="w-full flex items-center justify-between pt-3 mt-[24px] border-t border-black/15"
    >
      <div>
        <QuestionnaireButtonPrev
          @click="$emit('onStepBack')"
          :button-text="prevButtonText"
        />
      </div>
      <div @click="onSubmit">
        <QuestionnaireButtonNext
          :is-loading="false"
          :button-text="nextButtonText"
        />
      </div>
    </div>
  </div>
</template>
